import React, { createContext, useState, useEffect, useContext } from "react";

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_API_GATEWAY_URL;

if (!API_URL) {
  console.error("FATAL: REACT_APP_API_GATEWAY_URL is not set in .env");
}

// ─── SERVER-SIDE SIGNED REQUEST PROXY ───
// Request payload is signed on the server-side API route (/api/signed-fetch)
// so HMAC keys are never exposed in client JS bundles.
async function signedFetch(body) {
  try {
    const response = await fetch("/api/signed-fetch", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => null);
    if (response.ok && data) {
      return data;
    }
  } catch (err) {
    // Relative endpoint failed, fallback to direct port
  }

  try {
    const response = await fetch("http://localhost:5000/api/signed-fetch", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => null);
    if (data) {
      return data;
    }
  } catch (err) {
    console.warn("MongoDB API backend dispatch error:", err);
  }

  return { success: false, error: "Backend server connection unavailable." };
}

export { API_URL, signedFetch };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Auto-restore authenticated session with local fallback
  useEffect(() => {
    async function checkSession() {
      try {
        const data = await signedFetch({ action: "sessionCheck" });
        if (data && data.success && data.user) {
          setUser(data.user);
          setIsLoading(false);
          return;
        }
      } catch (err) {
        console.warn("API gateway session check fallback", err);
      }

      const savedUser = localStorage.getItem("hk_user");
      if (savedUser) {
        try {
          setUser(JSON.parse(savedUser));
        } catch (e) {
          setUser(null);
        }
      } else {
        setUser(null);
      }

      setIsLoading(false);
    }
    checkSession();
  }, []);

  /**
   * Secure User & Admin Login (Supports Super Admin & API Gateway)
   */
  const login = async (email, password) => {
    try {
      setIsLoading(true);

      const cleanEmail = (email || "").toLowerCase().trim();
      const cleanPassword = (password || "").trim();

      // Master Super Admin Instant Bypass
      if (
        cleanEmail === "admin@harikunjresort.com" &&
        (cleanPassword === "Admin@HariKunj2026" ||
         cleanPassword === "admin123" ||
         cleanPassword === "KJBYCXYKnkjsn@&$&243242" ||
         cleanPassword === "admin")
      ) {
        const superAdminUser = {
          userId: "ADMIN-001",
          fullName: "Super Admin (HariKunj)",
          email: "admin@harikunjresort.com",
          role: "admin",
        };
        setUser(superAdminUser);
        localStorage.setItem("hk_user", JSON.stringify(superAdminUser));
        return { success: true };
      }

      const data = await signedFetch({
        action: "login",
        email: cleanEmail,
        password: cleanPassword,
      });

      if (data && data.success) {
        setToken(data.token);
        setUser(data.user);
        localStorage.setItem("hk_user", JSON.stringify(data.user));
        return { success: true };
      }

      // Check local registered users if backend offline
      const localUsers = JSON.parse(localStorage.getItem("hk_registered_users") || "[]");
      const matchedLocalUser = localUsers.find(
        (u) => u.email.toLowerCase() === cleanEmail && u.password === cleanPassword
      );
      if (matchedLocalUser) {
        setUser(matchedLocalUser);
        localStorage.setItem("hk_user", JSON.stringify(matchedLocalUser));
        return { success: true };
      }

      return { success: false, error: data?.error || "Invalid email or password." };
    } catch (err) {
      console.error("Login server connection failed", err);
      // Fallback check for Master Super Admin in case of network error
      const cleanEmail = (email || "").toLowerCase().trim();
      const cleanPassword = (password || "").trim();
      if (
        cleanEmail === "admin@harikunjresort.com" &&
        (cleanPassword === "Admin@HariKunj2026" ||
         cleanPassword === "admin123" ||
         cleanPassword === "admin")
      ) {
        const superAdminUser = {
          userId: "ADMIN-001",
          fullName: "Super Admin (HariKunj)",
          email: "admin@harikunjresort.com",
          role: "admin",
        };
        setUser(superAdminUser);
        localStorage.setItem("hk_user", JSON.stringify(superAdminUser));
        return { success: true };
      }

      return {
        success: false,
        error: "Server connection failed. Kindly verify your internet connection.",
      };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Guest Registration (HMAC-signed)
   */
  const register = async (fullName, email, number, password) => {
    try {
      setIsLoading(true);

      const data = await signedFetch({
        action: "register",
        fullName,
        email,
        number,
        password,
      });

      if (data.success) {
        return { success: true };
      } else {
        return { success: false, error: data.error || "Registration failed." };
      }
    } catch (err) {
      console.error("Registration server connection failed", err);
      return {
        success: false,
        error: "Server connection failed. Kindly verify your internet connection.",
      };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Request Password Reset Link (Token-based)
   */
  const requestPasswordReset = async (email) => {
    try {
      setIsLoading(true);

      const data = await signedFetch({
        action: "requestPasswordReset",
        email,
      });

      if (data.success) {
        return { success: true, message: data.message };
      } else {
        return { success: false, error: data.error || "Failed to request password reset." };
      }
    } catch (err) {
      console.error("Password reset request connection failed", err);
      return {
        success: false,
        error: "Server connection failed. Kindly verify your internet connection.",
      };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Secure Password Reset (using single-use token)
   */
  const resetPassword = async (email, token, newPassword) => {
    try {
      setIsLoading(true);

      const data = await signedFetch({
        action: "resetPassword",
        email,
        token,
        newPassword,
      });

      if (data.success) {
        return { success: true, message: data.message };
      } else {
        return { success: false, error: data.error || "Password reset failed." };
      }
    } catch (err) {
      console.error("Password reset server connection failed", err);
      return {
        success: false,
        error: "Server connection failed. Kindly verify your internet connection.",
      };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Secure Session Terminate (Clears HttpOnly Cookie)
   */
  const logout = async () => {
    try {
      await signedFetch({ action: "logout" });
    } catch (err) {
      console.warn("Logout request note:", err);
    } finally {
      setToken(null);
      setUser(null);
      localStorage.removeItem("hk_auth_token");
      localStorage.removeItem("hk_auth_user");
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, token, login, register, logout, requestPasswordReset, resetPassword, isLoading }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
